import {createContext, useContext, useEffect, useState} from 'react';
import {
  getDoc,
  getFirestore,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import {useParams} from 'react-router-dom';
import {ONE_MINUTE_MS, useServerTime} from './TimeContext';
import {Role, RoleFirebaseString} from 'types/role';
import type {CourseId} from 'types/common';
import {getCourseDocumentRef} from '@cip/core/firebase/client';
import {
  resolveHiddenRichTextTools,
  resolveLinkedLessons,
  resolveLockedPages,
  SECTION_FEEDBACK_DELAY_DAYS_DEFAULT,
  SHADOW_REVIEW_DUE_WEEKDAY_DEFAULT,
  STUDENT_JOIN_GRACE_MINUTES_AFTER_START_DEFAULT,
  TARGET_LIVE_SECTION_SIZE_DEFAULT,
} from '@cip/core/firebase';
import type {
  CourseFeature,
  HideableRichTextTool,
  LinkedLesson,
  LockableCoursePage,
} from '@cip/core/firebase';
import {
  computeSectionInstant,
  getSectionClock,
  isFoothillCourse,
  type CourseWeekId,
  type SectionClock,
  type SectionWeekId,
} from '@cip/core/section';
import {useCourseWeeksInternal} from './useWeekHooks';
import type {LanguageCode} from 'components/translation/utils';
import {useSetCourseName} from './accessibility/AccessibilityContext';
import {firebaseStringToRole, roleToFirebaseString} from 'contexts/profileUtil';
import {retryFailedRead} from 'utils/retryFailedRead';

/**
 * The visual/behavioural style of a course. Controls UI chrome, branding, and
 * feature defaults throughout the app.
 *
 * - `'cip'`  — Code in Place (default when the field is absent in Firestore).
 * - `'math'` — Math 4 AI and similar maths-focused courses.
 */
export type CourseStyle = 'cip' | 'math';

/** Returns a valid {@link CourseStyle}, defaulting to `'cip'` when unrecognised. */
function parseCourseStyle(raw: unknown): CourseStyle {
  return raw === 'math' ? 'math' : 'cip';
}

/** What the course document says about who may enter the course. */
interface CourseGate {
  /** The course the answer was read for. */
  courseId: CourseId;
  /** Whether the course document exists. */
  exists: boolean;
  /** The course document's `minRole`, or `Role.UNREGISTERED` when unset. */
  minRole: Role;
}

export interface CourseContextType {
  /**
   * The id of the course.
   */
  courseId: CourseId;
  /**
   * Whether the course data is loading.
   */
  isLoading: boolean;
  /**
   * The error that occurred when loading the course data (if any).
   */
  error: Error | null;
  /**
   * Whether the course document exists. False until `isLoading` clears, so
   * check `isLoading` first to tell "not yet known" from "no such course".
   * Also false when the read failed, so check `error` before treating it as
   * "no such course".
   */
  courseExists: boolean;
  /**
   * The minimum role the course requires of its members, from the course
   * document's `minRole`. `Role.UNREGISTERED` when the course sets none,
   * which is also the value that applies no gate.
   */
  courseMinRole: Role;
  /**
   * The (human-readable) name of the course.
   */
  courseName: string;
  /**
   * The start date of the course. Formatted as an ISO 8601 string as
   * YYYY-MM-DDTHH:mm:ssXXX
   */
  courseStartDate: string;
  /**
   * The end date of the course. Formatted as an ISO 8601 string as
   * YYYY-MM-DDTHH:mm:ssXXX
   */
  courseEndDate: string;
  /**
   * The number of weeks in the course.
   */
  nCourseWeeks: number;
  /**
   * The current week of the course.
   */
  _courseLessonWeek: CourseWeekId;
  /**
   * The current week of the section.
   */
  _courseSectionWeek: SectionWeekId;
  /**
   * The timestamp of the first section of the course. Section start times are
   * calculated from this timestamp and the section time delta. Note that the
   * timezone of this field may not be specified and is hardcoded to UTC-7
   * (PDT). Formatted as an ISO 8601 string as YYYY-MM-DDTHH:mm:ss-07:00
   */
  firstSectionTimestamp: string;
  /**
   * The number of hours of section a week. In other words, the difference in
   * hours between the start of the first section and the start of the last
   * section in a week.
   */
  sectionTimeDelta: number;
  /**
   * How the course's section times recur, from `course.timezone` and
   * `course.sectionTimesFollowCourseTimezone`. Pass it to
   * `computeSectionInstant` and the other `@cip/core/section` helpers.
   */
  sectionClock: SectionClock;
  /**
   * Days after a week's last section ENDS before SL section feedback opens.
   * Course `sectionFeedbackDelayDays`, defaulting to
   * `SECTION_FEEDBACK_DELAY_DAYS_DEFAULT`.
   */
  sectionFeedbackDelayDays: number;
  /**
   * Minutes after a section's start during which a student may still join it
   * for the first time. Course `studentJoinGraceMinutesAfterStart`,
   * defaulting to `STUDENT_JOIN_GRACE_MINUTES_AFTER_START_DEFAULT`.
   */
  studentJoinGraceMinutesAfterStart: number;
  /**
   * Students per live section the reroute pickers fill toward first. Course
   * `targetLiveSectionSize`, defaulting to `TARGET_LIVE_SECTION_SIZE_DEFAULT`.
   */
  targetLiveSectionSize: number;
  /**
   * The weekday at whose end, Anywhere on Earth, peer-shadow reviews are due.
   * Course `shadowReviewDueWeekday`, defaulting to
   * `SHADOW_REVIEW_DUE_WEEKDAY_DEFAULT`.
   */
  shadowReviewDueWeekday: number;
  /**
   * Whether a feature is enabled for the course. By default, checks through the
   * student features; if isSl is true, checks through the section leader
   * features.
   */
  isFeatureEnabled: (featureName: CourseFeature, isSl?: boolean) => boolean;
  /**
   * Fetches the sections for the course.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  getSectionsSnapshot: () => any;
  /**
   * Gets the first day of a section.
   *
   * @deprecated Prefer `computeSectionInstant` from `@cip/core/section`
   *   with `{firstSectionTimestamp, timeIndex: sectionIndex, weekIndex: 0}`.
   *
   *   TODO: [#2364](https://github.com/CodeinPlace/cip/issues/2364) -
   *   migrate callers to `computeSectionInstant` and drop this from
   *   the context.
   */
  getFirstDayOfSection: (sectionIndex: number) => Date;
  /**
   * Gets the Date of the start first section (course-wide) for a given section
   * week. Typically a Wednesday at 3 PM PDT.
   *
   * @deprecated Prefer `computeSectionInstant` from `@cip/core/section`
   *   with `{firstSectionTimestamp, timeIndex: 0, weekIndex: sectionWeekId - 1}`.
   *
   *   TODO: [#2364](https://github.com/CodeinPlace/cip/issues/2364) -
   *   migrate callers and drop this from the context.
   */
  getFirstSectionOfWeekDate: (sectionWeekId: SectionWeekId) => Date;
  /**
   * Gets the Date of the start of the last section (course-wide) for a given
   * section week. Typically a Friday at 3 PM PDT.
   *
   * @deprecated Prefer `computeSectionInstant` plus an explicit
   *   `sectionTimeDelta - 1` hour offset.
   *
   *   TODO: [#2364](https://github.com/CodeinPlace/cip/issues/2364) -
   *   migrate callers and drop this from the context.
   */
  getLastSectionOfWeekDate: (sectionWeekId: SectionWeekId) => Date;
  /**
   * Gets the Date of the start of the last section (course-wide) for a given
   * section week. Typically a Friday at 3 PM PDT.
   */
  getLastSectionOfCourseDate: () => Date;
  /**
   * The features enabled for the course.
   */
  courseFeatures: CourseFeature[];
  /**
   * The features enabled for the section leaders.
   */
  slFeatures: CourseFeature[];
  /**
   * The features the course marks beta, which show a Beta badge.
   */
  betaFeatures: CourseFeature[];
  /**
   * The typesense index for the stories feature.
   */
  storiesTypesenseIndex: string;
  /**
   * The list of roles that have access to a roadmap for the course.
   */
  roadmapList: RoleFirebaseString[];
  /**
   * The link to use to authenticate with Canvas (if applicable).
   */
  courseCanvasAuthLink: string;
  /**
   * TODO: Remove this field. Only used by CS 105 to determine which instructors
   * are in the course. Instead, instructors should be fetched from a new
   * field.
   */
  isCodeInPlaceCourse: boolean;
  /**
   * Whether TeachNow is used in the course.
   */
  usesTeachNow: boolean;
  /**
   * Whether sections use an instructor-provided ("bring your own") Zoom link
   * instead of CIP-managed meeting creation. See `usesExternalZoom` on the
   * course document.
   */
  usesExternalZoom: boolean;
  /**
   * Whether the course teaches section from slide decks: the section page
   * shows a Slides column, and a section leader's Join Section button opens
   * the week's deck in a new tab before redirecting to Zoom. See
   * `usesSlidesForSection` on the course document.
   */
  usesSlidesForSection: boolean;
  /**
   * Whether the section page shows the weekly section leader Guide column.
   * See `usesSectionGuide` on the course document.
   */
  usesSectionGuide: boolean;
  /**
   * Whether the section page shows students a Reflection column. See
   * `usesSectionReflection` on the course document.
   */
  usesSectionReflection: boolean;
  /**
   * Whether the section page shows a Handout column. See
   * `usesSectionHandout` on the course document.
   */
  usesSectionHandout: boolean;
  /**
   * Whether the section page shows a Key column for the week's solution. See
   * `usesSectionSolution` on the course document.
   */
  usesSectionSolution: boolean;
  /**
   * Whether the section page shows section leaders a Lesson Plan column. See
   * `usesSectionLessonPlan` on the course document.
   */
  usesSectionLessonPlan: boolean;
  /**
   * Whether the course teaches section with twists. When off, the SL debrief
   * drops its per-student twist rating. See `usesSectionTwists` on the course
   * document.
   */
  usesSectionTwists: boolean;
  /** Firestore ids of the course's configured Super Section records. */
  superSectionIds: string[];
  /**
   * Pages whose sidebar entry reads "Coming soon" and whose route is closed
   * to non-staff. See `resolveLockedPages` in `@cip/core`.
   */
  lockedPages: readonly LockableCoursePage[];
  /**
   * Whether SL onboarding asks for a live section practice time. See
   * `hasSlLivePractice` on the course document.
   */
  hasSlLivePractice: boolean;
  /**
   * Whether section leaders can refer students during onboarding. See
   * `allowSlsToReferStudents` on the course document.
   */
  allowSlsToReferStudents: boolean;
  /**
   * Whether section leaders see unreleased student roadmap content, marked as
   * upcoming. See `showUpcomingContentToSls` on the course document.
   */
  showUpcomingContentToSls: boolean;
  /**
   * Toolbar buttons rich text editors in this course leave out. See
   * `resolveHiddenRichTextTools` in `@cip/core`.
   */
  hiddenRichTextTools: readonly HideableRichTextTool[];
  /**
   * Lessons from other courses listed on this course's Lessons page. See
   * `linkedLessons` on the course document.
   */
  linkedLessons: readonly LinkedLesson[];
  /**
   * Whether the course has grades.
   */
  courseHasGrades: boolean;
  /**
   * Whether the course is a Foothill College course.
   */
  isFoothill: boolean;
  /**
   * The visual/behavioural style of the course. Controls UI chrome, branding,
   * and feature defaults. Defaults to `'cip'` when not set in Firestore.
   * See {@link CourseStyle} for valid values.
   */
  courseStyle: CourseStyle;
  /**
   * The course's IDE style-feedback exclusion list, exactly as stored. Typed
   * `unknown` because nothing validates it on read, and reading it as a list
   * when it holds something else changes behavior silently — pass it to
   * `isStyleFeedbackAvailableForAssignment`, which parses it and falls back to
   * the built-in defaults. Consumers should go through
   * `useStyleFeedbackAvailability` rather than read this directly.
   */
  styleFeedbackExcludedAssignments: unknown;
  /**
   * The number of students allowed to be permanently assigned to a minor
   * section.
   */
  minorSectionCap: number;
  /**
   * The number of students allowed to be permanently assigned to a non-minor
   * section.
   */
  nonMinorSectionCap: number;
  /**
   * The default language for the course. Overrides the user's preferred
   * language if they navigate to the course in a language other than the
   * default.
   */
  courseDefaultLanguage?: LanguageCode;
  /**
   * Availability per hour across a section week for SL signup. Length equals sectionTimeDelta.
   */
  sectionLeaderSectionSignupAvailability?: boolean[];
  /**
   * The timestamp of the end of the section leader onboarding (prior to
   * training). Formatted as an ISO 8601 string as YYYY-MM-DDTHH:mm:ssXXX
   */
  slOnboardingEndDateTimestamp?: string;
  /**
   * When section leader onboarding opens, as an ISO 8601 string with a UTC
   * offset. Empty when the course sets none, which means no lower bound.
   */
  slOnboardingStartDateTimestamp?: string;
  /**
   * When mentor onboarding opens, as an ISO 8601 string with a UTC offset.
   * Empty when the course sets none, which means no lower bound.
   */
  mentorOnboardingStartDateTimestamp?: string;
  /**
   * When mentor onboarding closes, as an ISO 8601 string with a UTC offset.
   * Empty when the course sets none, which means no upper bound.
   */
  mentorOnboardingEndDateTimestamp?: string;
  /** Whether SL onboarding requires a backup weekly section time. */
  requireSlBackupSectionTime: boolean;
  /**
   * The number of weeks between the first and second section leader training
   * sessions. Training sessions are expected to happen on the same day and time
   * each week.
   */
  numWeeksBetweenTrainingSessions?: number;
  /**
   * The timestamp of the end of the student onboarding. Formatted as an ISO
   * 8601 string as YYYY-MM-DDTHH:mm:ssXXX
   */
  studentOnboardingEndDateTimestamp?: string;
  /**
   * The timestamp of the start of the student onboarding. Formatted as an ISO
   * 8601 string as YYYY-MM-DDTHH:mm:ssXXX
   */
  studentOnboardingStartDateTimestamp?: string;
  /**
   * The timestamp at which students are matched to their sections for the
   * current course offering. Used on the student Section page to tell
   * students who are not yet in a section when their section will be
   * assigned. When absent, the page falls back to the default message.
   * Formatted as an ISO 8601 string as YYYY-MM-DDTHH:mm:ssXXX.
   */
  sectionMatchingTimestamp?: string;
  /**
   * Whether students have been matched to their sections, as set by admins.
   * False until then, including when the course document has no value.
   */
  areSectionsMatched: boolean;
  /**
   * When TeachNow live teaching becomes available for the course. Formatted as
   * an ISO 8601 string as YYYY-MM-DDTHH:mm:ssXXX.
   */
  teachNowStartTimestamp?: string;
  /**
   * When TeachNow ends for the course. At/after this time every "Start
   * TeachNow" entry point is replaced with a "has ended" notice. Availability
   * is the half-open window `[teachNowStartTimestamp, teachNowEndTimestamp)`.
   * Absent means no scheduled end. Formatted as an ISO 8601 string as
   * YYYY-MM-DDTHH:mm:ssXXX.
   */
  teachNowEndTimestamp?: string;
}

const defaultData: CourseContextType = {
  courseId: '',
  courseName: '',
  courseStartDate: '',
  courseEndDate: '',
  nCourseWeeks: 0,
  isLoading: true,
  error: null,
  courseExists: false,
  courseMinRole: Role.UNREGISTERED,
  firstSectionTimestamp: '',
  _courseLessonWeek: 1 as CourseWeekId, // resets on monday
  _courseSectionWeek: 1 as SectionWeekId, // resets on friday
  courseFeatures: [],
  sectionTimeDelta: 0,
  sectionClock: {},
  sectionFeedbackDelayDays: SECTION_FEEDBACK_DELAY_DAYS_DEFAULT,
  studentJoinGraceMinutesAfterStart:
    STUDENT_JOIN_GRACE_MINUTES_AFTER_START_DEFAULT,
  targetLiveSectionSize: TARGET_LIVE_SECTION_SIZE_DEFAULT,
  shadowReviewDueWeekday: SHADOW_REVIEW_DUE_WEEKDAY_DEFAULT,
  slFeatures: [],
  betaFeatures: [],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  isFeatureEnabled: (featureName: CourseFeature, isSl?: boolean) => false,
  getSectionsSnapshot: () => {
    return null;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  getFirstDayOfSection: (sectionIndex: number) => {
    return null;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  getFirstSectionOfWeekDate: (sectionWeekId: SectionWeekId) => {
    return null;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  getLastSectionOfWeekDate: (sectionWeekId: SectionWeekId) => {
    return null;
  },
  getLastSectionOfCourseDate: () => {
    return null;
  },
  storiesTypesenseIndex: '',
  roadmapList: [],
  courseCanvasAuthLink: '',
  isCodeInPlaceCourse: false,
  usesTeachNow: false,
  usesExternalZoom: false,
  usesSlidesForSection: false,
  usesSectionGuide: true,
  usesSectionReflection: true,
  usesSectionHandout: false,
  usesSectionSolution: false,
  usesSectionLessonPlan: false,
  usesSectionTwists: true,
  superSectionIds: [],
  lockedPages: [],
  hasSlLivePractice: true,
  allowSlsToReferStudents: true,
  showUpcomingContentToSls: false,
  hiddenRichTextTools: [],
  linkedLessons: [],
  courseHasGrades: false,
  isFoothill: false,
  minorSectionCap: 0,
  nonMinorSectionCap: 0,
  sectionLeaderSectionSignupAvailability: [],
  slOnboardingEndDateTimestamp: '',
  slOnboardingStartDateTimestamp: '',
  mentorOnboardingStartDateTimestamp: '',
  mentorOnboardingEndDateTimestamp: '',
  requireSlBackupSectionTime: false,
  numWeeksBetweenTrainingSessions: 0,
  studentOnboardingEndDateTimestamp: '',
  studentOnboardingStartDateTimestamp: '',
  sectionMatchingTimestamp: '',
  areSectionsMatched: false,
  teachNowStartTimestamp: '',
  teachNowEndTimestamp: '',
  courseStyle: 'cip',
  styleFeedbackExcludedAssignments: undefined,
};

/**
 * TODO: there are two big refactors (one easy, one hard)
 *
 * 1. in some places in the database, weeks are 0 indexed, in others they are 1 indexed.
 *    This should be consistent (likely 0 indexed). When displaying to the user, we can add 1
 *
 * 2. This context uses a long list of useStates (each with their one setState). There is a
 *    great chance to reduce the amount of code.
 */

export const CourseContext = createContext<CourseContextType>(defaultData);

/**
 * Just the resolved course id, kept separate from {@link CourseContext} so
 * that consumers which only need the id (e.g. `useCourseId()`) don't
 * re-render on every course-data update. `CourseContext`'s value is a fresh
 * object on every internal state change (course features, weeks, ...); a
 * plain string prop only changes — and only triggers consumers — when the id
 * itself actually changes.
 */
export const CourseIdContext = createContext<CourseId>('');

/**
 * Reads and parses course data for the given courseId.
 */
export function useCourseContextForCourse(
  courseId: CourseId,
): CourseContextType {
  const setCourseNameAccessibility = useSetCourseName();
  const [courseName, setCourseName] = useState<string>('');
  const [courseStartDate, setCourseStartDate] = useState<string>('');
  const [courseEndDate, setCourseEndDate] = useState<string>('');
  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const [nCourseWeeks, setnCourseWeeks] = useState<number>(0);
  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const [firstSectionTimestamp, setFirstSectionTimeStamp] =
    useState<string>('');
  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const [courseCanvasAuthLink, setCanvasCourseAuthLink] = useState<string>('');
  const [courseHasGrades, setCourseHasGrades] = useState<boolean>(false);
  const [courseFeatures, setCourseFeatures] = useState<CourseFeature[]>([]);
  const [slFeatures, setSlFeatures] = useState<CourseFeature[]>([]);
  const [betaFeatures, setBetaFeatures] = useState<CourseFeature[]>([]);
  const [sectionTimeDelta, setSectionTimeDelta] = useState<number>(0);
  const [sectionClock, setSectionClock] = useState<SectionClock>({});
  const [sectionFeedbackDelayDays, setSectionFeedbackDelayDays] =
    useState<number>(SECTION_FEEDBACK_DELAY_DAYS_DEFAULT);
  const [
    studentJoinGraceMinutesAfterStart,
    setStudentJoinGraceMinutesAfterStart,
  ] = useState<number>(STUDENT_JOIN_GRACE_MINUTES_AFTER_START_DEFAULT);
  const [targetLiveSectionSize, setTargetLiveSectionSize] = useState<number>(
    TARGET_LIVE_SECTION_SIZE_DEFAULT,
  );
  const [shadowReviewDueWeekday, setShadowReviewDueWeekday] = useState<number>(
    SHADOW_REVIEW_DUE_WEEKDAY_DEFAULT,
  );
  const [minorSectionCap, setMinorSectionCap] = useState<number>(0);
  const [nonMinorSectionCap, setNonMinorSectionCap] = useState<number>(0);
  const [courseDefaultLanguage, setCourseDefaultLanguage] =
    useState<LanguageCode>();
  const [
    sectionLeaderSectionSignupAvailability,
    setSectionLeaderSectionSignupAvailability,
  ] = useState<boolean[]>([]);
  const [storiesTypesenseIndex, setStoriesTypesenseIndex] =
    useState<string>('');
  const [roadmapList, setRoadmapList] = useState<RoleFirebaseString[]>([
    'student',
    'sl',
  ]);
  const [isCodeInPlaceCourse, setIsCodeInPlaceCourse] =
    useState<boolean>(false);
  const [usesTeachNow, setUsesTeachNow] = useState<boolean>(false);
  const [usesExternalZoom, setUsesExternalZoom] = useState<boolean>(false);
  const [usesSlidesForSection, setUsesSlidesForSection] =
    useState<boolean>(false);
  const [usesSectionGuide, setUsesSectionGuide] = useState<boolean>(true);
  const [usesSectionReflection, setUsesSectionReflection] =
    useState<boolean>(true);
  const [usesSectionHandout, setUsesSectionHandout] = useState<boolean>(false);
  const [usesSectionSolution, setUsesSectionSolution] =
    useState<boolean>(false);
  const [usesSectionLessonPlan, setUsesSectionLessonPlan] =
    useState<boolean>(false);
  const [usesSectionTwists, setUsesSectionTwists] = useState<boolean>(true);
  const [superSectionIds, setSuperSectionIds] = useState<string[]>([]);
  const [lockedPages, setLockedPages] = useState<
    readonly LockableCoursePage[]
  >([]);
  const [hasSlLivePractice, setHasSlLivePractice] = useState<boolean>(true);
  const [allowSlsToReferStudents, setAllowSlsToReferStudents] =
    useState<boolean>(true);
  const [showUpcomingContentToSls, setShowUpcomingContentToSls] =
    useState<boolean>(false);
  const [hiddenRichTextTools, setHiddenRichTextTools] = useState<
    readonly HideableRichTextTool[]
  >([]);
  const [linkedLessons, setLinkedLessons] = useState<readonly LinkedLesson[]>(
    [],
  );
  const [numWeeksBetweenTrainingSessions, setNumWeeksBetweenTrainingSessions] =
    useState<number>(0);
  const [slOnboardingEndDateTimestamp, setSlOnboardingEndDateTimestamp] =
    useState<string>('');
  const [slOnboardingStartDateTimestamp, setSlOnboardingStartDateTimestamp] =
    useState<string>('');
  const [
    mentorOnboardingStartDateTimestamp,
    setMentorOnboardingStartDateTimestamp,
  ] = useState<string>('');
  const [mentorOnboardingEndDateTimestamp, setMentorOnboardingEndDateTimestamp] =
    useState<string>('');
  const [requireSlBackupSectionTime, setRequireSlBackupSectionTime] =
    useState<boolean>(false);
  const [
    studentOnboardingEndDateTimestamp,
    setStudentOnboardingEndDateTimestamp,
  ] = useState<string>('');
  const [
    studentOnboardingStartDateTimestamp,
    setStudentOnboardingStartDateTimestamp,
  ] = useState<string>('');
  const [sectionMatchingTimestamp, setSectionMatchingTimestamp] =
    useState<string>('');
  const [areSectionsMatched, setAreSectionsMatched] = useState<boolean>(false);
  const [teachNowStartTimestamp, setTeachNowStartTimestamp] =
    useState<string>('');
  const [teachNowEndTimestamp, setTeachNowEndTimestamp] =
    useState<string>('');
  const [courseStyle, setCourseStyle] = useState<CourseStyle>('cip');
  const [styleFeedbackExcludedAssignments, setStyleFeedbackExcludedAssignments] =
    useState<unknown>(undefined);

  const isFoothill = isFoothillCourse(courseId);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [courseGate, setCourseGate] = useState<CourseGate | null>(null);
  // The provider outlives a course switch, so a gate read for another course
  // must not answer for this one.
  const currentCourseGate =
    courseGate !== null && courseGate.courseId === courseId
      ? courseGate
      : null;
  const {_courseLessonWeek, _courseSectionWeek} = useCourseWeeksInternal({
    isLoading,
    firstSectionTimestamp,
    sectionTimeDelta,
    courseStartDate,
    sectionClock,
  });

  const db = getFirestore();
  const courseDocRef = getCourseDocumentRef({courseId});

  // An unparseable anchor yields an Invalid Date, as these helpers always
  // have, rather than computeSectionInstant's throw.
  const getSectionInstant = (timeIndex: number, weekIndex: number): Date =>
    Number.isNaN(Date.parse(firstSectionTimestamp))
      ? new Date(Number.NaN)
      : computeSectionInstant({
          firstSectionTimestamp,
          timeIndex,
          weekIndex,
          ...sectionClock,
        });

  const getFirstDayOfSection = (sectionIndex: number) => {
    // NOTE: this relies on `firstSectionTimestamp` being loaded.
    // Callers should guard on `CourseContext.isLoading` / `firstSectionTimestamp`
    // before invoking this function.
    if (!firstSectionTimestamp) {
      throw new Error('Timestamp not loaded yet');
    }
    return getSectionInstant(sectionIndex, 0);
  };

  function getFirstSectionOfWeekDate(sectionWeekId: SectionWeekId): Date {
    if (!firstSectionTimestamp) {
      throw new Error('First section timestamp is not loaded');
    }
    // NOTE: sectionWeekId is 1 indexed, so we subtract 1 to get the correct offset
    return getSectionInstant(0, sectionWeekId - 1);
  }

  function getLastSectionOfWeekDate(sectionWeekId: SectionWeekId): Date {
    if (!firstSectionTimestamp) {
      throw new Error('First section timestamp is not loaded');
    }
    return getSectionInstant(sectionTimeDelta, sectionWeekId - 1);
  }

  function getLastSectionOfCourseDate(): Date {
    if (!firstSectionTimestamp) {
      throw new Error('First section timestamp is not loaded');
    }
    // nCourseWeeks, like sectionWeekId, is 1 indexed
    const lastSectionDate = getLastSectionOfWeekDate(
      nCourseWeeks as SectionWeekId,
    );
    return lastSectionDate;
  }

  useEffect(() => {
    let isCurrentCourse = true;
    const abortController = new AbortController();
    const getCourseData = async () => {
      if (!courseId) return;

      setIsLoading(true);
      setError(null);
      const courseDoc = await retryFailedRead({
        read: () => getDoc(courseDocRef),
        signal: abortController.signal,
      });
      if (!isCurrentCourse) return;
      const minRoleString = courseDoc.data()?.minRole;
      setCourseGate({
        courseId,
        exists: courseDoc.exists(),
        minRole: minRoleString
          ? firebaseStringToRole(minRoleString)
          : Role.UNREGISTERED,
      });
      if (courseDoc.exists()) {
        const courseData = courseDoc.data();
        const safeCourseFeatures = courseData.features ?? [];
        const safeSlFeatures = courseData.slFeatures ?? [];
        setCourseName(courseData.name ?? '');
        setCourseStartDate(courseData.startDate ?? '');
        setnCourseWeeks(courseData.nWeeks ?? 0);
        // TODO: this is a hack to get the first section timestamp in the
        // correct timezone. We should remove this once we have a better
        // solution.
        setFirstSectionTimeStamp(courseData.firstSectionTimestamp ?? '');
        setCourseEndDate(courseData.endDate ?? '');
        setCourseFeatures(safeCourseFeatures);
        setSlFeatures(safeSlFeatures);
        setBetaFeatures(courseData.betaFeatures ?? []);
        try {
          setSectionTimeDelta(Number(courseData.sectionTimeDelta) ?? 0); // the number of hours of section a week
        } catch (e) {
          console.error('Error setting sectionTimeDelta', {
            courseData,
            e,
          });
          setSectionTimeDelta(0);
        }
        setSectionClock(getSectionClock(courseData));
        setSectionFeedbackDelayDays(
          courseData.sectionFeedbackDelayDays ??
            SECTION_FEEDBACK_DELAY_DAYS_DEFAULT,
        );
        setStudentJoinGraceMinutesAfterStart(
          courseData.studentJoinGraceMinutesAfterStart ??
            STUDENT_JOIN_GRACE_MINUTES_AFTER_START_DEFAULT,
        );
        setTargetLiveSectionSize(
          courseData.targetLiveSectionSize ?? TARGET_LIVE_SECTION_SIZE_DEFAULT,
        );
        setShadowReviewDueWeekday(
          courseData.shadowReviewDueWeekday ??
            SHADOW_REVIEW_DUE_WEEKDAY_DEFAULT,
        );
        setStoriesTypesenseIndex(courseData.storiesTypesenseIndex ?? '');
        setCanvasCourseAuthLink(courseData.canvasAuthLink ?? '');
        setCourseHasGrades(safeSlFeatures.includes('grades'));
        const verboseRoadmapList = [
          'student',
          'sl',
          ...(courseData.roadmapList ?? ['student', 'sl']),
        ];
        const uniqueRoadmap = array =>
          array.filter((v, i) => array.indexOf(v) === i);
        setRoadmapList(uniqueRoadmap(verboseRoadmapList));
        setIsCodeInPlaceCourse(courseData.isCodeInPlaceCourse || false);
        setUsesTeachNow(courseData.usesTeachNow || true);
        // Defaults OFF: a course is CIP-managed Zoom unless it explicitly opts
        // into the instructor-provided link mode.
        setUsesExternalZoom(courseData.usesExternalZoom ?? false);
        setUsesSlidesForSection(
          courseData.usesSlidesForSection ??
            courseData.opensSlidesOnSectionJoin ??
            false,
        );
        // Default ON: the guide and the students' reflection lesson are the
        // section page's normal weekly links. A course opts out when it does
        // not keep the documents they read.
        setUsesSectionGuide(courseData.usesSectionGuide ?? true);
        setUsesSectionReflection(courseData.usesSectionReflection ?? true);
        setUsesSectionHandout(courseData.usesSectionHandout ?? false);
        setUsesSectionSolution(courseData.usesSectionSolution ?? false);
        setUsesSectionLessonPlan(courseData.usesSectionLessonPlan ?? false);
        setUsesSectionTwists(courseData.usesSectionTwists ?? true);
        setSuperSectionIds(
          Array.isArray(courseData.superSectionIds)
            ? courseData.superSectionIds
            : [],
        );
        setLockedPages(resolveLockedPages({course: courseData}));
        setHasSlLivePractice(courseData.hasSlLivePractice ?? true);
        setAllowSlsToReferStudents(courseData.allowSlsToReferStudents ?? true);
        setShowUpcomingContentToSls(
          courseData.showUpcomingContentToSls ?? false,
        );
        setHiddenRichTextTools(
          resolveHiddenRichTextTools({course: courseData}),
        );
        setLinkedLessons(resolveLinkedLessons({course: courseData}));
        setMinorSectionCap(courseData.minorSectionCap || 10);
        setNonMinorSectionCap(courseData.nonMinorSectionCap || 10);
        setCourseDefaultLanguage(courseData.defaultLanguage);
        // Default availability to all-true of length sectionTimeDelta if missing or wrong length
        const incomingAvailability: boolean[] =
          courseData.sectionLeaderSectionSignupAvailability ?? [];
        const desiredLen = Number(courseData.sectionTimeDelta) ?? 0;
        let normalizedAvailability: boolean[];
        if (
          !incomingAvailability ||
          incomingAvailability.length !== desiredLen
        ) {
          normalizedAvailability = Array.from({length: desiredLen}, () => true);
        } else {
          normalizedAvailability = incomingAvailability.map(v => Boolean(v));
        }
        setSectionLeaderSectionSignupAvailability(normalizedAvailability);
        setNumWeeksBetweenTrainingSessions(
          courseData.numWeeksBetweenTrainingSessions ?? 0,
        );
        setSlOnboardingEndDateTimestamp(
          courseData.slOnboardingEndDateTimestamp ?? '',
        );
        setSlOnboardingStartDateTimestamp(
          courseData.slOnboardingStartDateTimestamp ?? '',
        );
        setMentorOnboardingStartDateTimestamp(
          courseData.mentorOnboardingStartDateTimestamp ?? '',
        );
        setMentorOnboardingEndDateTimestamp(
          courseData.mentorOnboardingEndDateTimestamp ?? '',
        );
        setRequireSlBackupSectionTime(
          courseData.requireSlBackupSectionTime ?? false,
        );
        setStudentOnboardingEndDateTimestamp(
          courseData.studentOnboardingEndDateTimestamp ?? '',
        );
        setStudentOnboardingStartDateTimestamp(
          courseData.studentOnboardingStartDateTimestamp ?? '',
        );
        setSectionMatchingTimestamp(
          courseData.sectionMatchingTimestamp ?? '',
        );
        setAreSectionsMatched(courseData.areSectionsMatched === true);
        setTeachNowStartTimestamp(courseData.teachNowStartTimestamp ?? '');
        setTeachNowEndTimestamp(courseData.teachNowEndTimestamp ?? '');
        setCourseStyle(parseCourseStyle(courseData.courseStyle));
        setStyleFeedbackExcludedAssignments(
          courseData.styleFeedbackExcludedAssignments,
        );
      }
      setIsLoading(false);
    };
    getCourseData().catch(e => {
      if (!isCurrentCourse) return;
      setError(e);

      setIsLoading(false);
    });
    return () => {
      isCurrentCourse = false;
      abortController.abort();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [courseId]);

  useEffect(() => {
    setCourseNameAccessibility(courseName);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [courseName]);

  const isFeatureEnabled = (featureName: CourseFeature, isSl: boolean = false) => {
    const features = isSl ? slFeatures : courseFeatures;
    return features.includes(featureName);
  };

  const getSectionsSnapshot = async () => {
    // fetches sections for this course
    const sectionRef = collection(db, 'sections');
    const q = query(sectionRef, where('courseId', '==', courseId));
    const querySnapshot = await getDocs(q);
    return querySnapshot;
  };

  return {
    courseId,
    isLoading: isLoading || (currentCourseGate === null && error === null),
    error,
    courseExists: currentCourseGate?.exists ?? false,
    courseMinRole: currentCourseGate?.minRole ?? Role.UNREGISTERED,
    courseName,
    courseStartDate,
    courseEndDate,
    nCourseWeeks,
    firstSectionTimestamp,
    courseFeatures,
    slFeatures,
    betaFeatures,
    _courseSectionWeek,
    _courseLessonWeek,
    isFeatureEnabled,
    getSectionsSnapshot,
    sectionTimeDelta,
    sectionClock,
    sectionFeedbackDelayDays,
    studentJoinGraceMinutesAfterStart,
    targetLiveSectionSize,
    shadowReviewDueWeekday,
    getFirstDayOfSection,
    getFirstSectionOfWeekDate,
    getLastSectionOfWeekDate,
    getLastSectionOfCourseDate,
    storiesTypesenseIndex,
    roadmapList,
    courseCanvasAuthLink,
    isCodeInPlaceCourse,
    usesTeachNow,
    usesExternalZoom,
    usesSlidesForSection,
    usesSectionGuide,
    usesSectionReflection,
    usesSectionHandout,
    usesSectionSolution,
    usesSectionLessonPlan,
    usesSectionTwists,
    superSectionIds,
    lockedPages,
    hasSlLivePractice,
    allowSlsToReferStudents,
    showUpcomingContentToSls,
    hiddenRichTextTools,
    linkedLessons,
    courseHasGrades,
    isFoothill,
    minorSectionCap,
    nonMinorSectionCap,
    courseDefaultLanguage,
    sectionLeaderSectionSignupAvailability,
    slOnboardingEndDateTimestamp,
    slOnboardingStartDateTimestamp,
    mentorOnboardingStartDateTimestamp,
    mentorOnboardingEndDateTimestamp,
    requireSlBackupSectionTime,
    numWeeksBetweenTrainingSessions,
    studentOnboardingEndDateTimestamp,
    studentOnboardingStartDateTimestamp,
    sectionMatchingTimestamp,
    areSectionsMatched,
    teachNowStartTimestamp,
    teachNowEndTimestamp,
    courseStyle,
    styleFeedbackExcludedAssignments,
  };
}

/**
 * Hook to check if section leader onboarding is closed for a course. Uses
 * real-time server time to determine if the onboarding end timestamp has
 * passed.
 *
 * @param slOnboardingEndDateTimestamp - The end timestamp for SL onboarding
 * @returns boolean - true if onboarding is closed, false if open or no
 * timestamp set
 */
export function useIsSLOnboardingClosed(
  slOnboardingEndDateTimestamp?: string,
): boolean {
  // We don't need to be that precise, so make it a long refresh rate to avoid
  // unnecessary re-renders.
  const serverTimeMs = useServerTime(15 * ONE_MINUTE_MS);

  if (!slOnboardingEndDateTimestamp || !serverTimeMs) {
    return false;
  }

  return new Date(slOnboardingEndDateTimestamp) < new Date(serverTimeMs);
}

/**
 * Provides a CourseContext for the user's current course.
 * This is a wrapper around the useCourseContextForCourse hook.
 *
 * @param courseId - The resolved course id, supplied when this provider is
 *   mounted ABOVE the `:courseId` route (see `CourseScopeLayout` in
 *   Router.tsx, which also resolves the `/apply/:targetCourseId` short link
 *   to the target's parent course — an ancestor layout cannot read that
 *   param itself). When omitted, falls back to the `:courseId` URL param,
 *   the right default for providers mounted directly on a `:courseId` route
 *   (e.g. the showcase route).
 */
export function CourseProvider({
  children,
  courseId: courseIdOverride,
}: {
  children: React.ReactNode;
  courseId?: CourseId;
}) {
  const {courseId: courseIdParam} = useParams();
  const courseId = courseIdOverride ?? courseIdParam;
  const courseContext = useCourseContextForCourse(courseId);

  return (
    <CourseIdContext.Provider value={courseId ?? ''}>
      <CourseContext.Provider value={courseContext}>
        {children}
      </CourseContext.Provider>
    </CourseIdContext.Provider>
  );
}

export {isFoothillCourse};

export const useDoesCourseSupportExperiencedStudents = (): {
  doesSupportExperiencedStudents: boolean;
  isLoading: boolean;
} => {
  const {roadmapList, isLoading} = useContext(CourseContext);
  if (isLoading) return {doesSupportExperiencedStudents: false, isLoading};
  return {
    doesSupportExperiencedStudents: roadmapList.includes(
      roleToFirebaseString(Role.EXPERIENCED_STUDENT),
    ),
    isLoading,
  };
};
